import { computed } from 'vue';
import { useCreateFieldGetter } from '@/common/customisation/composables';
import { PUI } from '@pleased-npm/fe-customisation';
import type { TextSubmitFormCustomisation } from '@/components/TextSubmitForm.types';

/**
 * Composable for Open Request description field (ValidationInput textarea in OpenRequestModal).
 */
export const useOpenRequestCustomisation = () => {
  const { getField: getTextBoxField } = useCreateFieldGetter({
    registryId: PUI.REGISTRY_IDS.CHATBOT_OPEN_REQUEST,
    sectionKey: PUI.CHATBOT_OPEN_REQUEST_SECTION_KEYS.TEXT_BOX,
    blockKey: PUI.CHATBOT_OPEN_REQUEST_BLOCK_KEYS.TEXT_BLOCK_SUBMIT,
  });

  const { getField: getCancelButtonField } = useCreateFieldGetter({
    registryId: PUI.REGISTRY_IDS.CHATBOT_OPEN_REQUEST,
    sectionKey: PUI.CHATBOT_OPEN_REQUEST_SECTION_KEYS.CANCEL_BUTTON,
    blockKey: PUI.CHATBOT_OPEN_REQUEST_BLOCK_KEYS.TEXT_BLOCK_SUBMIT,
  });

  const { getField: getSubmitButtonField } = useCreateFieldGetter({
    registryId: PUI.REGISTRY_IDS.CHATBOT_OPEN_REQUEST,
    sectionKey: PUI.CHATBOT_OPEN_REQUEST_SECTION_KEYS.SUBMIT_BUTTON,
    blockKey: PUI.CHATBOT_OPEN_REQUEST_BLOCK_KEYS.TEXT_BLOCK_SUBMIT,
  });

  // Text box customisation
  const textBoxPlaceholder = getTextBoxField(PUI.COMMON_FIELD_KEYS.PLACEHOLDER_TEXT);
  const textBoxBackgroundColor = getTextBoxField(PUI.COMMON_FIELD_KEYS.BACKGROUND_COLOR);
  const textBoxTextColor = getTextBoxField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);
  const textBoxBorderColor = getTextBoxField(PUI.COMMON_FIELD_KEYS.BORDER_COLOR);
  const textBoxPlaceholderColor = getTextBoxField(PUI.COMMON_FIELD_KEYS.PLACEHOLDER_COLOR);

  // Cancel button customisation
  const cancelButtonText = getCancelButtonField(PUI.COMMON_FIELD_KEYS.BUTTON_TEXT);
  const cancelButtonBackgroundColor = getCancelButtonField(PUI.COMMON_FIELD_KEYS.BUTTON_BACKGROUND_COLOR);
  const cancelButtonBorderColor = getCancelButtonField(PUI.COMMON_FIELD_KEYS.BUTTON_BORDER_COLOR);
  const cancelButtonTextColor = getCancelButtonField(PUI.COMMON_FIELD_KEYS.BUTTON_TEXT_COLOR);

  // Submit button customisation
  const submitButtonText = getSubmitButtonField(PUI.COMMON_FIELD_KEYS.BUTTON_TEXT);
  const submitButtonBackgroundColor = getSubmitButtonField(PUI.COMMON_FIELD_KEYS.BUTTON_BACKGROUND_COLOR);
  const submitButtonBorderColor = getSubmitButtonField(PUI.COMMON_FIELD_KEYS.BUTTON_BORDER_COLOR);
  const submitButtonTextColor = getSubmitButtonField(PUI.COMMON_FIELD_KEYS.BUTTON_TEXT_COLOR);

  // Disabled submit button customisation
  const disabledSubmitButtonBackgroundColor = getSubmitButtonField(
    PUI.COMMON_FIELD_KEYS.DISABLED_BUTTON_BACKGROUND_COLOR
  );
  const disabledSubmitButtonBorderColor = getSubmitButtonField(PUI.COMMON_FIELD_KEYS.DISABLED_BUTTON_BORDER_COLOR);
  const disabledSubmitButtonTextColor = getSubmitButtonField(PUI.COMMON_FIELD_KEYS.DISABLED_BUTTON_TEXT_COLOR);

  // Compose TextSubmitFormCustomisation object
  const customisation = computed<TextSubmitFormCustomisation>(() => ({
    input: {
      backgroundColor: textBoxBackgroundColor.value,
      textColor: textBoxTextColor.value,
      borderColor: textBoxBorderColor.value,
      placeholderColor: textBoxPlaceholderColor.value,
    },
    cancelButton: {
      text: cancelButtonText.value,
      backgroundColor: cancelButtonBackgroundColor.value,
      borderColor: cancelButtonBorderColor.value,
      textColor: cancelButtonTextColor.value,
    },
    submitButton: {
      text: submitButtonText.value,
      backgroundColor: submitButtonBackgroundColor.value,
      borderColor: submitButtonBorderColor.value,
      textColor: submitButtonTextColor.value,
    },
    submitButtonDisabled: {
      backgroundColor: disabledSubmitButtonBackgroundColor.value,
      borderColor: disabledSubmitButtonBorderColor.value,
      textColor: disabledSubmitButtonTextColor.value,
    },
  }));

  return {
    customisation,
    placeholderText: textBoxPlaceholder,
  };
};
